import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { userNeedsCompanySelection, userNeedsRoleSelection, getUserPendingRequests } from '@/services/companyService';
import { LoadingSpinner } from '@/components/ui/loading-spinner';

interface CompanyRedirectProps {
  children: React.ReactNode;
}

const CompanyRedirect: React.FC<CompanyRedirectProps> = ({ children }) => {
  const { user, loading: authLoading, isInitialized } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkUserStatus = async () => {
      // Wait for auth to be fully initialized before checking
      if (!isInitialized || authLoading) {
        return;
      }

      if (!user) {
        setIsChecking(false);
        return;
      }

      try {
        // Skip check for auth-related pages, role selection, and company selection pages
        const skipPaths = ['/auth', '/auth/callback', '/role-selection', '/company-selection', '/manager/company', '/consultant/company'];
        if (skipPaths.some(path => location.pathname.startsWith(path))) {
          setIsChecking(false);
          return;
        }

        // First check if user needs role selection
        const needsRoleSelection = await userNeedsRoleSelection(user.id);
        
        if (needsRoleSelection) {
          navigate('/role-selection', { replace: true });
          return;
        }

        // Then check if user needs company selection
        const needsCompanySelection = await userNeedsCompanySelection(user.id);
        
        if (needsCompanySelection) {
          // Check for pending requests
          // If offline, skip pending requests check
          const pending = (typeof navigator !== 'undefined' && navigator && navigator.onLine === false)
            ? { requests: [] }
            : await getUserPendingRequests(user.id);
          
          if (pending.requests.length > 0) {
            // User has pending requests, redirect to company selection to show pending status
            navigate('/company-selection', { replace: true });
            return;
          }
          
          // No pending requests, redirect to company selection
          navigate('/company-selection', { replace: true });
          return;
        }

        setIsChecking(false);
      } catch (error) {
        console.error('Error checking user status:', error);
        // On network/transient errors, do not redirect; allow user to remain on current route
        setIsChecking(false);
      }
    };

    checkUserStatus();
  }, [user, location.pathname, navigate, isInitialized, authLoading]);

  if (isChecking || !isInitialized || authLoading) {
    return <LoadingSpinner size="lg" />;
  }

  return <>{children}</>;
};

export default CompanyRedirect; 